/**
 * Desk country picker. Inference uses the dial catalog the API returns.
 * The generated fallback covers the same table, only for when that GET is down — not a second engine.
 */

import type { SipProfile } from "./types";
import { ISO_COUNTRIES } from "./countries.generated";

export interface DialCountry {
  iso2: string;
  name: string;
  calling_code: string;
}

/** Full dial table, generated from the vendor source. Used only if the catalog GET fails. */
export const FALLBACK_COUNTRIES: readonly DialCountry[] = ISO_COUNTRIES;

const SUGGEST_LANG: Record<string, string> = {
  CH: "de",
  GB: "en",
  US: "en",
  CA: "en",
  DE: "de",
  FR: "fr",
  BE: "fr",
  IT: "it",
  NL: "nl",
  IE: "en",
  AU: "en",
  BR: "pt",
  ZA: "en",
  NG: "en",
  IN: "en",
};

export function suggestLanguage(iso2: string, codes: readonly string[]): string {
  const suggested = SUGGEST_LANG[iso2.trim().toUpperCase()] ?? "";
  if (!suggested) return "";
  return codes.includes(suggested) ? suggested : "";
}

/** Longest calling-code prefix. Ambiguous prefixes (NANP +1) return empty — staff pick. */
export function inferCountry(dest: string, catalog: readonly DialCountry[]): string {
  const digits = dest.replace(/\D/g, "").replace(/^00/, "");
  if (digits.length < 3) return "";
  let best = 0;
  const hits: DialCountry[] = [];
  for (const row of catalog) {
    const cc = row.calling_code.replace(/\D/g, "");
    if (!cc || !digits.startsWith(cc)) continue;
    if (cc.length > best) {
      best = cc.length;
      hits.length = 0;
      hits.push(row);
    } else if (cc.length === best) {
      hits.push(row);
    }
  }
  return hits.length === 1 ? hits[0].iso2 : "";
}

export function countryByIso(iso2: string, catalog: readonly DialCountry[]): DialCountry | undefined {
  const key = iso2.trim().toUpperCase();
  return catalog.find((row) => row.iso2 === key);
}

export function trunkAllowsCountry(
  allowed: readonly string[] | undefined,
  iso2: string,
  callingCode: string,
): boolean {
  const list = (allowed ?? []).map((item) => item.trim()).filter(Boolean);
  if (!list.length) return true;
  const iso = iso2.trim().toUpperCase();
  const cc = callingCode.replace(/\D/g, "");
  return list.some((token) => {
    if (token.toUpperCase() === iso) return true;
    const digits = token.replace(/\D/g, "");
    return Boolean(cc) && digits === cc;
  });
}

/** Trunk allowlist ∩ catalog. Empty allowlist = full catalog. */
export function countriesForSip(
  catalog: readonly DialCountry[],
  sips: readonly SipProfile[],
  sipId: string,
): DialCountry[] {
  const pinned = sipId.trim();
  const sip = pinned
    ? sips.find((item) => item.sofia_gateway_id === pinned)
    : sips.find((item) => item.active) ?? sips[0];
  const allowed = (sip?.allowed_countries ?? []).map((item) => item.trim()).filter(Boolean);
  if (!allowed.length) return [...catalog];

  const rows = catalog.filter((row) => trunkAllowsCountry(allowed, row.iso2, row.calling_code));
  const named = new Set(rows.map((row) => row.iso2.toUpperCase()));
  for (const token of allowed) {
    const iso = token.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(iso) || named.has(iso)) continue;
    // Catalog has no name for this allowlist ISO2 — show the code, don't invent a deck.
    rows.push({ iso2: iso, name: "", calling_code: "" });
    named.add(iso);
  }
  return rows;
}

/**
 * Why Dial must stay grey for this country, or null when the pin is allowed.
 * Empty SIP follows the active trunk.
 */
export function trunkCountryBlock(
  sips: readonly SipProfile[],
  sipId: string,
  iso2: string,
  callingCode: string,
): string | null {
  if (!iso2) return null;
  const pinned = sipId.trim();
  if (pinned) {
    const row = sips.find((item) => item.sofia_gateway_id === pinned);
    if (!row) return null;
    if (trunkAllowsCountry(row.allowed_countries, iso2, callingCode)) return null;
    return `SIP trunk ${pinned} does not allow destination country ${iso2}`;
  }
  if (!sips.length) return null;
  const active = sips.find((item) => item.active) ?? sips[0];
  if (!active) return null;
  if (trunkAllowsCountry(active.allowed_countries, iso2, callingCode)) return null;
  const any = sips.some((item) => trunkAllowsCountry(item.allowed_countries, iso2, callingCode));
  if (!any) return `No SIP trunk allows destination country ${iso2}`;
  return `Active trunk ${active.sofia_gateway_id} does not allow ${iso2} — pick another SIP`;
}

export function parseCountryList(raw: string): string[] {
  return raw
    .split(/[,\s]+/)
    .map((item) => item.trim().toUpperCase())
    .filter(Boolean);
}
